import { Bike, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

// Jun-2026 (User-Wunsch) — Ersatzfahrzeug-/Abholservice-Badges am Werkstatt-Termin:
// Mietvertrag (PDF) + Übergabe-Verknüpfung, Partner-Zu-/Absage erfassen, Erinnerung senden.
export default function BookingExtrasBadges({ booking: b, onChanged }) {
    if (!b?.loaner_requested && !b?.pickup_service_requested) return null;

    const setPartnerResponse = async (response) => {
        try {
            await api.post(`/workshop/bookings/${b.id}/partner-response`, { response });
            toast.success(response === "accepted" ? "Zusage erfasst" : response === "declined" ? "Absage erfasst" : "Antwort zurückgesetzt");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };
    const remindPartner = async () => {
        try {
            await api.post(`/workshop/bookings/${b.id}/partner-remind`);
            toast.success("Erinnerung an den Partner gesendet (bzw. zur E-Mail-Freigabe eingereicht)");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erinnerung fehlgeschlagen"); }
    };
    const openRentalContract = async () => {
        try {
            const res = await api.get(`/dealer/rental-bookings/${b.loaner_rental_booking_id}/contract.pdf`, { responseType: "blob" });
            window.open(URL.createObjectURL(res.data), "_blank", "noopener");
        } catch { toast.error("Mietvertrag konnte nicht geladen werden"); }
    };

    return (
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`booking-extras-${b.id}`}>
            {b.loaner_requested && (b.loaner_rental_booking_id ? (
                <>
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider" title="Mietbuchung wurde automatisch im Ersatzfahrzeug-Bereich angelegt">
                        <Bike className="w-3 h-3" /> Ersatzfahrzeug reserviert{b.loaner_vehicle_title ? `: ${b.loaner_vehicle_title}` : ""}
                    </span>
                    <button type="button" onClick={openRentalContract}
                        className="text-[10px] font-bold underline underline-offset-2 text-gray-600 hover:text-[#111827]"
                        title="Mietvertrag als PDF öffnen — zum Unterschreiben bei der Übergabe"
                        data-testid={`booking-rental-contract-${b.id}`}>Mietvertrag (PDF)</button>
                    <a href="/dealer/workshop-orders?tab=rental"
                        className="text-[10px] font-bold underline underline-offset-2 text-gray-600 hover:text-[#111827]"
                        title="Zum Ersatzfahrzeug-Bereich: Übergabe (KM, Ausweis, Unterschrift) und Rückgabe erfassen"
                        data-testid={`booking-rental-handover-${b.id}`}>Übergabe/Rückgabe →</a>
                </>
            ) : (
                <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-800 border border-orange-300 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider">
                    <Bike className="w-3 h-3" /> Ersatzfahrzeug angefragt{b.loaner_unavailable ? " — keines verfügbar!" : ""}
                </span>
            ))}
            {b.pickup_service_requested && (
                <>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${
                        b.pickup_partner_response === "accepted" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                            : b.pickup_partner_response === "declined" ? "bg-red-50 text-red-700 border-red-300"
                                : b.pickup_partner_notified ? "bg-amber-50 text-amber-800 border-amber-300"
                                    : "bg-orange-50 text-orange-800 border-orange-300"}`}
                        data-testid={`booking-partner-status-${b.id}`}>
                        <Truck className="w-3 h-3" /> Abholservice{b.pickup_partner_name ? `: ${b.pickup_partner_name}` : ""}
                        {b.pickup_partner_response === "accepted" ? " — ZUGESAGT ✓"
                            : b.pickup_partner_response === "declined" ? " — ABGESAGT ✗"
                                : b.pickup_partner_notified ? " — Antwort ausstehend"
                                    : b.pickup_partner_no_email ? " (ohne E-Mail — bitte anrufen)" : ""}
                    </span>
                    {b.pickup_partner_id && !b.pickup_partner_response && (
                        <>
                            <button type="button" onClick={() => setPartnerResponse("accepted")}
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                title="Partner hat zugesagt" data-testid={`booking-partner-accept-${b.id}`}>✓ Zugesagt</button>
                            <button type="button" onClick={() => setPartnerResponse("declined")}
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border border-red-300 text-red-600 hover:bg-red-50"
                                title="Partner hat abgesagt" data-testid={`booking-partner-decline-${b.id}`}>✗ Abgesagt</button>
                            {b.pickup_partner_notified && (
                                <button type="button" onClick={remindPartner}
                                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border border-amber-300 text-amber-700 hover:bg-amber-50"
                                    title={b.pickup_partner_reminded_at ? `Zuletzt erinnert: ${new Date(b.pickup_partner_reminded_at).toLocaleString("de-DE")}` : "Erinnerungs-E-Mail an den Partner senden"}
                                    data-testid={`booking-partner-remind-${b.id}`}>Erinnern</button>
                            )}
                        </>
                    )}
                    {b.pickup_partner_response && (
                        <button type="button" onClick={() => setPartnerResponse(null)}
                            className="text-[10px] text-gray-400 hover:text-gray-600 underline underline-offset-2"
                            title="Antwort zurücksetzen" data-testid={`booking-partner-reset-${b.id}`}>zurücksetzen</button>
                    )}
                    {b.pickup_address && <span className="text-[10px] text-gray-500">{b.pickup_address}</span>}
                </>
            )}
        </div>
    );
}
